// Durable native token storage bridge.
//
// On Capacitor (Android WebView / iOS WKWebView) `localStorage` is NOT reliably
// persisted across cold starts, so users were logged out on every launch. This
// module mirrors the session tokens into `@capacitor/preferences` (backed by
// Android SharedPreferences / iOS UserDefaults), which IS durable, and hydrates
// them back into `localStorage` on cold start BEFORE the first `getMe()`.
//
// Web is a pure no-op fast path: `localStorage` stays the single source of
// truth and no Capacitor plugin is touched.

import { Capacitor } from '@capacitor/core';

// Same keys the rest of the app already reads/writes in localStorage.
export const ACCESS_KEY = 'dutchdate_token';
export const REFRESH_KEY = 'dutchdate_refresh';
export const MFA_DEVICE_KEY = 'dutchdate_mfa_device';
export const DEVICE_ID_KEY = 'dutchdate_device_id';

const ALL_KEYS = [ACCESS_KEY, REFRESH_KEY, MFA_DEVICE_KEY, DEVICE_ID_KEY];

const isNative = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Lazy-load the plugin so the web bundle never pulls it into the critical path.
let prefsPromise: Promise<any> | null = null;
function getPrefs(): Promise<any> {
  if (!prefsPromise) {
    prefsPromise = import('@capacitor/preferences')
      .then((m) => m.Preferences)
      .catch(() => null);
  }
  return prefsPromise;
}

// Fire-and-forget durable write. localStorage is written by the caller; this
// only propagates to native so we don't block synchronous code paths.
export function mirrorToNative(key: string, value: string): void {
  if (!isNative()) return;
  getPrefs()
    .then((prefs) => prefs?.set({ key, value }))
    .catch(() => {});
}

export function removeFromNative(key: string): void {
  if (!isNative()) return;
  getPrefs()
    .then((prefs) => prefs?.remove({ key }))
    .catch(() => {});
}

// Cold-start hydration. Native wins over a possibly-evicted WebView value; if
// native is empty we migrate any existing localStorage value up into native so
// existing installs become durable without a re-login.
export async function hydrateAuthTokens(): Promise<void> {
  if (!isNative()) return;
  const prefs = await getPrefs();
  if (!prefs) return;
  await Promise.all(
    ALL_KEYS.map(async (key) => {
      try {
        const { value } = await prefs.get({ key });
        if (value !== null && value !== undefined) {
          try { localStorage.setItem(key, value); } catch { /* storage off */ }
        } else {
          let legacy: string | null = null;
          try { legacy = localStorage.getItem(key); } catch { /* storage off */ }
          if (legacy !== null) await prefs.set({ key, value: legacy });
        }
      } catch {
        /* best-effort: never block app boot on storage */
      }
    }),
  );
}
